import Dexie, { Table } from 'dexie';
import { Employee, Job, TimeEntry, TruckTicket, PendingAction } from '../types/backend.types';

// T&M ticket stored for offline submission
export interface PendingTMTicket {
  id?: number;
  job_id: number;
  job_name: string;
  employee_id: number;
  ticket_data: {
    employees: Array<{ employee_id: number; hours_worked: number }>;
    equipment: Array<any>;
    materials: Array<any>;
    dumpsters: Array<any>;
    superintendent_name: string;
    superintendent_email: string;
    signature_base64: string | null;
    notes: string;
    work_description: string;
    unsigned_reason: string | null;
    start_time: string | null;
    stop_time?: string | null;
    attachments: Array<{ filename: string; data: string }>;
    pre_uploaded_photos: Array<{ fullUrl: string; thumbnailUrl?: string }>;
    // Original creation timestamp - server should use this instead of GETDATE()
    original_created_at: string;
  };
  created_at: string;
  retry_count: number;
  last_error?: string;
}

class OfflineDatabase extends Dexie {
  employees!: Table<Employee>;
  jobs!: Table<Job>;
  time_entries!: Table<TimeEntry>;
  truck_tickets!: Table<TruckTicket>;
  pending_actions!: Table<PendingAction>;
  pending_tm_tickets!: Table<PendingTMTicket>;

  constructor() {
    super('BradannaFieldDB');

    // Version 1: Original schema
    this.version(1).stores({
      employees: 'employee_id',
      jobs: 'job_id',
      time_entries: 'entry_id',
      truck_tickets: 'ticket_id',
      pending_actions: '++id, type, timestamp'
    });

    // Version 2: Add pending T&M tickets table
    this.version(2).stores({
      employees: 'employee_id',
      jobs: 'job_id',
      time_entries: 'entry_id',
      truck_tickets: 'ticket_id',
      pending_actions: '++id, type, timestamp',
      pending_tm_tickets: '++id, job_id, employee_id, created_at'
    });
  }
}

export const db = new OfflineDatabase();

export const offlineService = {
  async queueAction(action: Omit<PendingAction, 'id'>) {
    return db.pending_actions.add(action);
  },

  async getPendingActions() {
    return db.pending_actions.toArray();
  },

  async clearPendingAction(id: number) {
    return db.pending_actions.delete(id);
  },

  async clearAllPendingActions() {
    return db.pending_actions.clear();
  },

  async saveJobs(jobs: Job[]) {
    return db.jobs.bulkPut(jobs);
  },

  async getJobs() {
    return db.jobs.toArray();
  },

  async saveEmployees(employees: Employee[]) {
    return db.employees.bulkPut(employees);
  },

  async getEmployees() {
    return db.employees.toArray();
  },

  // Clock status caching for offline resilience
  saveClockStatus(employeeId: number, status: {
    currentStatus: 'clocked_in' | 'not_clocked_in',
    currentEntry: TimeEntry | null,
    clockedInAtShop: boolean,
    isTemporaryShopClock: boolean,
    shopClockInTime: string | null
  }) {
    localStorage.setItem(`clock_status_${employeeId}`, JSON.stringify({
      ...status,
      lastUpdated: new Date().toISOString()
    }));
  },

  getCachedClockStatus(employeeId: number): {
    currentStatus: 'clocked_in' | 'not_clocked_in',
    currentEntry: TimeEntry | null,
    clockedInAtShop: boolean,
    isTemporaryShopClock: boolean,
    shopClockInTime: string | null,
    lastUpdated: string
  } | null {
    const cached = localStorage.getItem(`clock_status_${employeeId}`);
    if (!cached) return null;

    try {
      const parsed = JSON.parse(cached);
      // Cache expires after 24 hours to prevent stale data
      const lastUpdated = new Date(parsed.lastUpdated);
      const hoursSinceUpdate = (Date.now() - lastUpdated.getTime()) / (1000 * 60 * 60);

      if (hoursSinceUpdate > 24) {
        localStorage.removeItem(`clock_status_${employeeId}`);
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  },

  clearClockStatus(employeeId: number) {
    localStorage.removeItem(`clock_status_${employeeId}`);
  },

  // T&M Ticket offline queue methods
  async queueTMTicket(ticket: Omit<PendingTMTicket, 'id'>) {
    return db.pending_tm_tickets.add(ticket);
  },

  async getPendingTMTickets() {
    return db.pending_tm_tickets.toArray();
  },

  async getPendingTMTicketCount() {
    return db.pending_tm_tickets.count();
  },

  async clearPendingTMTicket(id: number) {
    return db.pending_tm_tickets.delete(id);
  },

  async updateTMTicketRetry(id: number, error: string) {
    const ticket = await db.pending_tm_tickets.get(id);
    if (ticket) {
      return db.pending_tm_tickets.update(id, {
        retry_count: (ticket.retry_count || 0) + 1,
        last_error: error
      });
    }
  }
};
